import React from "react";
import { Button, If, makeClassName } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	BooleanField,
	DateTimeField,
	Form,
	FormType,
	RadioField,
	SubFormField,
	SubFormRenderProps,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import { NestedCreateSharingMenu } from "../../Components/SharingRecord";
import {
	MediaAttachmentFormValues,
	SharingRecordCustomFormValues,
	StudentAcademicNoteFormValues,
	StudentAttendanceNoteFormValues,
	StudentBehaviorNoteFormValues,
	StudentHealthNoteFormValues,
	StudentIncidentNoteFormValues,
	StudentInjuryNoteFormValues,
	StudentMedicationNoteFormValues,
	StudentNoteFormValues,
	StudentSocialEmotionalNoteFormValues,
} from "../../FormUtilities";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFieldProps } from "../shared";
import { StudentSelectField } from "../Student";
import { StudentAcademicNoteForm } from "../StudentAcademicNote";
import { StudentAttendanceNoteForm } from "../StudentAttendanceNote";
import { StudentBehaviorNoteForm } from "../StudentBehaviorNote";
import { StudentHealthNoteForm } from "../StudentHealthNote";
import { StudentIncidentNoteForm } from "../StudentIncidentNote";
import { StudentInjuryNoteForm } from "../StudentInjuryNote";
import { StudentMedicationNoteForm } from "../StudentMedicationNote";
import { StudentSocialEmotionalNoteForm } from "../StudentSocialEmotionalNote";
import { UserSelectField } from "../User";

/**
 * Generic props for fields of the StudentNote model.
 */
type FieldProps<K extends keyof StudentNoteFormValues.Base = keyof StudentNoteFormValues.Base> = BaseFieldProps<
	Pick<StudentNoteFormValues.Base, K>
>;

/**
 * Generic props for fields of the StudentNote model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof StudentNoteFormValues.Create = keyof StudentNoteFormValues.Create> =
	BaseFieldProps<Pick<StudentNoteFormValues.Create, K>>;

/**
 * Generic props for fields of the StudentNote model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof StudentNoteFormValues.Detail = keyof StudentNoteFormValues.Detail> =
	BaseFieldProps<Pick<StudentNoteFormValues.Detail, K>>;

/**
 * Field component for the `noteType` field of the StudentNote model.
 */
export function NoteType({ formState, ...props }: FieldProps<"noteType">) {
	return (
		<RadioField
			formState={formState}
			name="noteType"
			options={StudentNoteFormValues.noteTypeOptions}
			blankValue={null}
			{...props}
		/>
	);
}

/**
 * Field component for the `createdAt` field of the StudentNote model.
 */
export function CreatedAt({ formState, ...props }: DetailFieldProps<"createdAt">) {
	return (
		<DateTimeField
			formState={formState}
			name="createdAt"
			optional={FormType.isCreate(formState.formType)}
			precision="minute"
			{...props}
		/>
	);
}

/**
 * Field component for the `body` field of the StudentNote model.
 */
export function Body({ formState, ...props }: FieldProps<"body">) {
	return <TextField formState={formState} name="body" multiLine Input={BodyInput} {...props} />;
}

function BodyInput(props: TextFieldInputProps) {
	return <TextAreaInput {...props} minRows={4} />;
}

/**
 * Field component for the `isPinned` field of the StudentNote model.
 */
export function IsPinned({ formState, ...props }: FieldProps<"isPinned">) {
	return <BooleanField formState={formState} name="isPinned" {...props} />;
}

/**
 * Field component for the `requiresResolution` field of the StudentNote model.
 */
export function RequiresResolution({ formState, ...props }: FieldProps<"requiresResolution">) {
	return <BooleanField formState={formState} name="requiresResolution" {...props} />;
}

/**
 * Field component for the `isResolved` field of the StudentNote model.
 */
export function IsResolved({ formState, ...props }: FieldProps<"isResolved">) {
	return <BooleanField formState={formState} name="isResolved" {...props} />;
}

/**
 * Field component for the `resolvedAt` field of the StudentNote model.
 */
export function ResolvedAt({ formState, ...props }: FieldProps<"resolvedAt">) {
	return <DateTimeField formState={formState} name="resolvedAt" optional precision="minute" {...props} />;
}

/**
 * Field component for the `resolutionDescription` field of the StudentNote model.
 */
export function ResolutionDescription({
	formState,
	label = "Resolution",
	...props
}: FieldProps<"resolutionDescription">) {
	return (
		<TextField
			formState={formState}
			name="resolutionDescription"
			label={label}
			optional
			Input={ResolutionDescriptionInput}
			{...props}
		/>
	);
}

function ResolutionDescriptionInput(props: TextFieldInputProps) {
	return <TextAreaInput minRows={3} {...props} />;
}

export namespace Author {
	export type SelectProps = DetailFieldProps<"authorID"> &
		Omit<UserSelectField.SelectProps<StudentNoteFormValues.Detail>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `author` edge of the StudentNote model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="authorID" optional {...props} />;
	}
}

export namespace MediaAttachments {
	/**
	 * SubForm field component for the `mediaAttachments` edge of the StudentNote model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"mediaAttachments">) {
		return (
			<MediaAttachmentCustomForm.SubForm
				formState={formState}
				name="mediaAttachments"
				blankItem={MediaAttachmentFormValues.blankItemInStudentNote}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</MediaAttachmentCustomForm.SubForm>
		);
	}

	SubForm.SubForm = function (props: SubFormRenderProps<MediaAttachmentFormValues.CreateInStudentNote>) {
		return (
			<MediaAttachmentCustomForm.SubForm.SubForm {...props}>
				<MediaAttachmentCustomForm.CreateFieldsInStudentNote formState={props.formState} />
			</MediaAttachmentCustomForm.SubForm.SubForm>
		);
	};

	SubForm.AddButton = function (
		props: AddSubFormButtonRenderProps<
			Pick<StudentNoteFormValues.Create, "mediaAttachments">,
			MediaAttachmentFormValues.CreateInStudentNote
		>,
	) {
		return <MediaAttachmentCustomForm.SubForm.AddButton {...props} />;
	};
}

export namespace PrimaryStudents {
	export type SelectProps = FieldProps<"primaryStudentIDs"> &
		Omit<StudentSelectField.MultiSelectProps<StudentNoteFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `primaryStudents` edge of the StudentNote model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentSelectField.MultiSelect formState={formState} name="primaryStudentIDs" minItems={1} {...props} />;
	}
}

export namespace SecondaryStudents {
	export type SelectProps = FieldProps<"secondaryStudentIDs"> &
		Omit<StudentSelectField.MultiSelectProps<StudentNoteFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `secondaryStudents` edge of the StudentNote model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentSelectField.MultiSelect formState={formState} name="secondaryStudentIDs" {...props} />;
	}
}

export namespace SharingRecord {
	/**
	 * SubForm field component for the `sharingRecord` edge of the StudentNote model.
	 */
	export function SubForm({ formState, noHeader = true, className, ...props }: CreateFieldProps<"sharingRecord">) {
		return (
			<SubFormField
				formState={formState}
				name="sharingRecord"
				blankItem={SharingRecordCustomFormValues.blankItemInParent}
				minItems={1}
				maxItems={1}
				formattedBlank=""
				noHeader={noHeader}
				className={makeClassName("sharing-record-sub-form-field", className)}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({ formState }: SubFormRenderProps<SharingRecordCustomFormValues.CreateInParent>) {
		return <NestedCreateSharingMenu formState={formState} />;
	};

	SubForm.AddButton = function (
		_: AddSubFormButtonRenderProps<
			Pick<StudentNoteFormValues.Create, "sharingRecord">,
			SharingRecordCustomFormValues.CreateInParent
		>,
	) {
		return <React.Fragment></React.Fragment>;
	};
}

export namespace StudentAcademicNote {
	/**
	 * SubForm field component for the `studentAcademicNote` edge of the StudentNote model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentAcademicNote">) {
		return (
			<SubFormField
				formState={formState}
				name="studentAcademicNote"
				blankItem={StudentAcademicNoteFormValues.blankItemInStudentNote}
				minItems={1}
				maxItems={1}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<StudentAcademicNoteFormValues.CreateInStudentNote>) {
		return (
			<Form.Column style={{ width: "100%" }}>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove Student Academic Note
					</Button>
				</If>
				<StudentAcademicNoteForm.CreateFieldsInStudentNote formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<
		Pick<StudentNoteFormValues.Create, "studentAcademicNote">,
		StudentAcademicNoteFormValues.CreateInStudentNote
	>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add Student Academic Note
				</Button>
			</If>
		);
	};
}

export namespace StudentAttendanceNote {
	/**
	 * SubForm field component for the `studentAttendanceNote` edge of the StudentNote model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentAttendanceNote">) {
		return (
			<SubFormField
				formState={formState}
				name="studentAttendanceNote"
				blankItem={StudentAttendanceNoteFormValues.blankItemInStudentNote}
				minItems={1}
				maxItems={1}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<StudentAttendanceNoteFormValues.CreateInStudentNote>) {
		return (
			<Form.Column style={{ width: "100%" }}>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove Student Attendance Note
					</Button>
				</If>
				<StudentAttendanceNoteForm.CreateFieldsInStudentNote formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<
		Pick<StudentNoteFormValues.Create, "studentAttendanceNote">,
		StudentAttendanceNoteFormValues.CreateInStudentNote
	>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add Student Attendance Note
				</Button>
			</If>
		);
	};
}

export namespace StudentBehaviorNote {
	/**
	 * SubForm field component for the `studentBehaviorNote` edge of the StudentNote model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentBehaviorNote">) {
		return (
			<SubFormField
				formState={formState}
				name="studentBehaviorNote"
				blankItem={StudentBehaviorNoteFormValues.blankItemInStudentNote}
				minItems={1}
				maxItems={1}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<StudentBehaviorNoteFormValues.CreateInStudentNote>) {
		return (
			<Form.Column style={{ width: "100%" }}>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove Student Behavior Note
					</Button>
				</If>
				<StudentBehaviorNoteForm.CreateFieldsInStudentNote formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<
		Pick<StudentNoteFormValues.Create, "studentBehaviorNote">,
		StudentBehaviorNoteFormValues.CreateInStudentNote
	>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add Student Behavior Note
				</Button>
			</If>
		);
	};
}

export namespace StudentHealthNote {
	/**
	 * SubForm field component for the `studentHealthNote` edge of the StudentNote model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentHealthNote">) {
		return (
			<SubFormField
				formState={formState}
				name="studentHealthNote"
				blankItem={StudentHealthNoteFormValues.blankItemInStudentNote}
				minItems={1}
				maxItems={1}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<StudentHealthNoteFormValues.CreateInStudentNote>) {
		return (
			<Form.Column style={{ width: "100%" }}>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove Student Health Note
					</Button>
				</If>
				<StudentHealthNoteForm.CreateFieldsInStudentNote formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<
		Pick<StudentNoteFormValues.Create, "studentHealthNote">,
		StudentHealthNoteFormValues.CreateInStudentNote
	>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add Student Health Note
				</Button>
			</If>
		);
	};
}

export namespace StudentIncidentNote {
	/**
	 * SubForm field component for the `studentIncidentNote` edge of the StudentNote model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentIncidentNote">) {
		return (
			<SubFormField
				formState={formState}
				name="studentIncidentNote"
				blankItem={StudentIncidentNoteFormValues.blankItemInStudentNote}
				minItems={1}
				maxItems={1}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<StudentIncidentNoteFormValues.CreateInStudentNote>) {
		return (
			<Form.Column style={{ width: "100%" }}>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove Student Incident Note
					</Button>
				</If>
				<StudentIncidentNoteForm.CreateFieldsInStudentNote formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<
		Pick<StudentNoteFormValues.Create, "studentIncidentNote">,
		StudentIncidentNoteFormValues.CreateInStudentNote
	>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add Student Incident Note
				</Button>
			</If>
		);
	};
}

export namespace StudentInjuryNote {
	/**
	 * SubForm field component for the `studentInjuryNote` edge of the StudentNote model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentInjuryNote">) {
		return (
			<SubFormField
				formState={formState}
				name="studentInjuryNote"
				blankItem={StudentInjuryNoteFormValues.blankItemInStudentNote}
				minItems={1}
				maxItems={1}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<StudentInjuryNoteFormValues.CreateInStudentNote>) {
		return (
			<Form.Column style={{ width: "100%" }}>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove Student Injury Note
					</Button>
				</If>
				<StudentInjuryNoteForm.CreateFieldsInStudentNote formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<
		Pick<StudentNoteFormValues.Create, "studentInjuryNote">,
		StudentInjuryNoteFormValues.CreateInStudentNote
	>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add Student Injury Note
				</Button>
			</If>
		);
	};
}

export namespace StudentMedicationNote {
	/**
	 * SubForm field component for the `studentMedicationNote` edge of the StudentNote model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentMedicationNote">) {
		return (
			<SubFormField
				formState={formState}
				name="studentMedicationNote"
				blankItem={StudentMedicationNoteFormValues.blankItemInStudentNote}
				minItems={1}
				maxItems={1}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<StudentMedicationNoteFormValues.CreateInStudentNote>) {
		return (
			<Form.Column style={{ width: "100%" }}>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove Student Medication Note
					</Button>
				</If>
				<StudentMedicationNoteForm.CreateFieldsInStudentNote formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<
		Pick<StudentNoteFormValues.Create, "studentMedicationNote">,
		StudentMedicationNoteFormValues.CreateInStudentNote
	>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add Student Medication Note
				</Button>
			</If>
		);
	};
}

export namespace StudentSocialEmotionalNote {
	/**
	 * SubForm field component for the `studentSocialEmotionalNote` edge of the StudentNote model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentSocialEmotionalNote">) {
		return (
			<SubFormField
				formState={formState}
				name="studentSocialEmotionalNote"
				blankItem={StudentSocialEmotionalNoteFormValues.blankItemInStudentNote}
				minItems={1}
				maxItems={1}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<StudentSocialEmotionalNoteFormValues.CreateInStudentNote>) {
		return (
			<Form.Column style={{ width: "100%" }}>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove Student Social Emotional Note
					</Button>
				</If>
				<StudentSocialEmotionalNoteForm.CreateFieldsInStudentNote formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<
		Pick<StudentNoteFormValues.Create, "studentSocialEmotionalNote">,
		StudentSocialEmotionalNoteFormValues.CreateInStudentNote
	>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add Student Social Emotional Note
				</Button>
			</If>
		);
	};
}
